import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormGroup,
  Checkbox,
  Typography,
  Box,
  LinearProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
  Download as DownloadIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ro } from 'date-fns/locale';
import clientDashboardService from '@services/clientDashboardService';

interface ExportDataDialogProps {
  open: boolean;
  onClose: () => void;
}

const ENTITY_OPTIONS = [
  { id: 'profile', label: 'Profil & cont', description: 'Datele personale si setarile contului' },
  { id: 'animals', label: 'Animale', description: 'Informatii despre animalele tale' },
  { id: 'appointments', label: 'Programari', description: 'Istoric programari si consultatii' },
  { id: 'visits', label: 'Vizite medicale', description: 'Istoricul medical si diagnostic' },
  { id: 'documents', label: 'Documente (metadate)', description: 'Informatii despre documente (fara fisierele in sine)' },
  { id: 'messages', label: 'Mesaje', description: 'Conversatii cu clinicile' },
  { id: 'invoices', label: 'Facturi/Plati', description: 'Istoric facturi si plati' },
  { id: 'notifications', label: 'Notificari', description: 'Istoric notificari' },
];

const ExportDataDialog: React.FC<ExportDataDialogProps> = ({ open, onClose }) => {
  const [format, setFormat] = useState<'csv' | 'xlsx' | 'json'>('xlsx');
  const [entities, setEntities] = useState<string[]>(['profile', 'animals', 'appointments']);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<'idle' | 'pending' | 'processing' | 'ready' | 'failed'>('idle');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [filename, setFilename] = useState<string | null>(null);

  const handleEntityToggle = (entityId: string) => {
    setEntities(prev =>
      prev.includes(entityId)
        ? prev.filter(e => e !== entityId)
        : [...prev, entityId]
    );
  };

  const handleSelectAll = () => {
    if (entities.length === ENTITY_OPTIONS.length) {
      setEntities([]);
    } else {
      setEntities(ENTITY_OPTIONS.map(e => e.id));
    }
  };

  const handleStartExport = async () => {
    if (entities.length === 0) {
      setError('Selectati cel putin o categorie pentru export');
      return;
    }

    setLoading(true);
    setError('');
    setStatus('pending');
    setProgress(0);

    try {
      const params = {
        format,
        entities,
        dateFrom: dateFrom ? dateFrom.toISOString().split('T')[0] : undefined,
        dateTo: dateTo ? dateTo.toISOString().split('T')[0] : undefined,
        timezone: 'Europe/Bucharest',
      };

      const response = await clientDashboardService.startExport(params);

      if (response.download_url) {
        // Export sincron - gata imediat
        setStatus('ready');
        setProgress(100);
        setDownloadUrl(response.download_url);
        setFilename(response.filename || 'export.xlsx');
        setLoading(false); // Important: reset loading pentru export sincron
      } else if (response.export_id) {
        // Export asincron - polling pentru status
        startPolling(response.export_id);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Eroare la generarea exportului');
      setStatus('failed');
      setLoading(false);
    }
  };

  const startPolling = async (id: string) => {
    const pollInterval = setInterval(async () => {
      try {
        const statusResponse = await clientDashboardService.getExportStatus(id);
        
        setStatus(statusResponse.status);
        setProgress(statusResponse.progress || 0);

        if (statusResponse.status === 'ready') {
          clearInterval(pollInterval);
          setDownloadUrl(statusResponse.download_url || null);
          setFilename(statusResponse.filename || 'export.xlsx');
          setLoading(false);
        } else if (statusResponse.status === 'failed') {
          clearInterval(pollInterval);
          setError(statusResponse.error_message || 'Exportul a esuat');
          setLoading(false);
        }
      } catch (err) {
        clearInterval(pollInterval);
        setError('Eroare la verificarea statusului');
        setLoading(false);
      }
    }, 2000); // Poll la 2 secunde

    // Stop polling dupa 2 minute
    setTimeout(() => {
      clearInterval(pollInterval);
      if (status !== 'ready' && status !== 'failed') {
        setError('Timeout - exportul dureaza prea mult. Verificati mai tarziu.');
        setLoading(false);
      }
    }, 120000);
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormat('xlsx');
      setEntities(['profile', 'animals', 'appointments']);
      setDateFrom(null);
      setDateTo(null);
      setError('');
      setProgress(0);
      setStatus('idle');
      setDownloadUrl(null);
      setFilename(null);
      onClose();
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'pending':
        return 'Exportul este in coada...';
      case 'processing':
        return `Se genereaza exportul... ${progress}%`;
      case 'ready':
        return 'Export gata de descarcare!';
      case 'failed':
        return 'Exportul a esuat';
      default:
        return '';
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ro}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Export date personale</Typography>
            <IconButton onClick={handleClose} disabled={loading} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Informatii GDPR */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Conform GDPR, aveti dreptul sa exportati toate datele personale stocate in sistem.
              Acest export va contine DOAR datele dvs. si va fi disponibil pentru descarcare timp de 7 zile.
            </Typography>
          </Alert>

          {/* Format selection */}
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Format export
            </Typography>
            <RadioGroup row value={format} onChange={(e) => setFormat(e.target.value as any)}>
              <FormControlLabel value="xlsx" control={<Radio />} label="Excel (XLSX)" />
              <FormControlLabel value="csv" control={<Radio />} label="CSV (ZIP)" />
              <FormControlLabel value="json" control={<Radio />} label="JSON" />
            </RadioGroup>
          </Box>

          {/* Date range */}
          <Box mb={3}>
            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
              Interval de timp (optional)
            </Typography>
            <Box display="flex" gap={2} mt={1}>
              <DatePicker
                label="De la"
                value={dateFrom}
                onChange={(newValue) => setDateFrom(newValue)}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
              <DatePicker
                label="Pana la"
                value={dateTo}
                onChange={(newValue) => setDateTo(newValue)}
                minDate={dateFrom || undefined}
                slotProps={{ textField: { size: 'small', fullWidth: true } }}
              />
            </Box>
          </Box>

          {/* Entity selection */}
          <Box mb={2}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                Ce doriti sa exportati?
              </Typography>
              <Button size="small" onClick={handleSelectAll}>
                {entities.length === ENTITY_OPTIONS.length ? 'Deselecteaza tot' : 'Selecteaza tot'}
              </Button>
            </Box>
            <FormGroup>
              {ENTITY_OPTIONS.map(entity => (
                <FormControlLabel
                  key={entity.id}
                  control={
                    <Checkbox
                      checked={entities.includes(entity.id)}
                      onChange={() => handleEntityToggle(entity.id)}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {entity.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {entity.description}
                      </Typography>
                    </Box>
                  }
                />
              ))}
            </FormGroup>
          </Box>

          {/* Progress & Status */}
          {(loading || status !== 'idle') && (
            <Box mb={2}>
              {loading && status !== 'ready' && (
                <>
                  <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" align="center">
                    {getStatusMessage()}
                  </Typography>
                </>
              )}

              {status === 'ready' && downloadUrl && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Exportul este gata! Fisier: <strong>{filename}</strong>
                  </Typography>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownload}
                    size="small"
                    sx={{ mt: 1 }}
                  >
                    Descarca acum
                  </Button>
                </Alert>
              )}
            </Box>
          )}

          {/* Error */}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Info footer */}
          {status === 'idle' && (
            <Box mt={3} p={2} bgcolor="grey.50" borderRadius={1}>
              <Typography variant="caption" color="text.secondary" display="flex" alignItems="center" gap={0.5}>
                <InfoIcon fontSize="small" />
                Daca datele sunt multe, vom rula exportul in fundal si veti primi un link de descarcare cand este gata.
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {status === 'ready' ? 'Inchide' : 'Anuleaza'}
          </Button>
          {status !== 'ready' && (
            <Button
              onClick={handleStartExport}
              variant="contained"
              disabled={loading || entities.length === 0}
              startIcon={<DownloadIcon />}
            >
              Genereaza export
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default ExportDataDialog;

